import React from "react";
import {
  Card as MuiCard,
  CardContent,
  CardActions,
  CardProps as MuiCardProps,
} from "@mui/material";
import { BaseMoleculeProps, ComponentVariant } from "../../types";
import {
  createMemoizedComponent,
  useRenderPerformance,
  logBundleInfo,
} from "../../utils/performance";

export interface CardProps
  extends Omit<MuiCardProps, "variant">,
    Omit<BaseMoleculeProps, "variant"> {
  /**
   * Card elevation level (0-24)
   */
  elevation?: number;

  /**
   * Card variant with mobile-first considerations
   */
  variant?: ComponentVariant | "outlined";

  /**
   * Card header content
   */
  header?: React.ReactNode;

  /**
   * Card footer/actions content
   */
  actions?: React.ReactNode;

  /**
   * Padding variant for mobile-first design
   */
  padding?: "none" | "small" | "medium" | "large";

  /**
   * Loading state
   */
  loading?: boolean;

  /**
   * Click handler for interactive cards
   */
  onClick?: () => void;

  /**
   * Hover effect enabled
   */
  hoverable?: boolean;
}

const CardComponent: React.FC<CardProps> = ({
  elevation = 2,
  variant = "default",
  header,
  actions,
  padding = "medium",
  loading = false,
  onClick,
  hoverable = false,
  className,
  "data-testid": dataTestId,
  children,
  ...props
}) => {
  // Performance monitoring
  useRenderPerformance("Card");

  // Log bundle info in development
  React.useEffect(() => {
    logBundleInfo("Card");
  }, []);

  const paddingMap = {
    none: 0,
    small: { xs: 1, md: 1.5 },
    medium: { xs: 2, md: 3 },
    large: { xs: 3, md: 4 },
  };

  const cardProps: MuiCardProps = {
    elevation: variant === "outlined" ? 0 : elevation,
    variant: variant === "outlined" ? "outlined" : "elevation",
    onClick,
    className,
    sx: {
      // Mobile-first responsive styling
      borderRadius: {
        xs: "8px", // Mobile - smaller radius
        md: "12px", // Desktop - larger radius
      },
      // Padding based on variant
      "& .MuiCardContent-root": {
        padding: {
          xs:
            typeof paddingMap[padding] === "object"
              ? `${paddingMap[padding].xs}rem`
              : `${paddingMap[padding]}rem`,
          md:
            typeof paddingMap[padding] === "object"
              ? `${paddingMap[padding].md}rem`
              : `${paddingMap[padding]}rem`,
        },
        "&:last-child": {
          paddingBottom: {
            xs:
              typeof paddingMap[padding] === "object"
                ? `${paddingMap[padding].xs}rem`
                : `${paddingMap[padding]}rem`,
            md:
              typeof paddingMap[padding] === "object"
                ? `${paddingMap[padding].md}rem`
                : `${paddingMap[padding]}rem`,
          },
        },
      },
      // Compact variant
      ...(variant === "compact" && {
        "& .MuiCardContent-root": {
          padding: { xs: "8px", md: "12px" },
          "&:last-child": { paddingBottom: { xs: "8px", md: "12px" } },
        },
      }),
      // Expanded variant
      ...(variant === "expanded" && {
        "& .MuiCardContent-root": {
          padding: { xs: "24px", md: "32px" },
          "&:last-child": { paddingBottom: { xs: "24px", md: "32px" } },
        },
      }),
      // Hover effect
      ...(hoverable && {
        cursor: onClick ? "pointer" : "default",
        transition: "transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out",
        "&:hover": {
          transform: "translateY(-2px)",
          boxShadow: (theme) => theme.shadows[elevation + 2],
        },
      }),
      // Loading state
      ...(loading && {
        opacity: 0.7,
        pointerEvents: "none",
      }),
    },
    ...props,
  };

  return (
    <MuiCard {...cardProps}>
      {header}
      <CardContent>{children}</CardContent>
      {actions && (
        <CardActions
          sx={{
            padding: {
              xs: "8px 16px",
              md: "12px 24px",
            },
          }}
        >
          {actions}
        </CardActions>
      )}
    </MuiCard>
  );
};

// Apply memoization with performance monitoring
export const Card = createMemoizedComponent(CardComponent);

// Set display name for debugging
(Card as any).displayName = "Card";
